import { useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import { http } from "@/lib/api";
import MessagesListener from "@/components/MessagesListener";

/**
 * Global side-effects that must run for EVERY authenticated user
 * regardless of which layout their role renders under.
 *
 * The desktop layout (managers, cashiers) uses ``Layout.jsx``; the
 * waiter role short-circuits straight to ``MobileRestaurant.jsx`` with
 * no sidebar. Before Feb 2026 the presence heartbeat + messaging SSE
 * lived inside Layout, which meant waiters were invisible on the
 * «Χειριστές Online» list AND never received incoming messages —
 * silently breaking the whole inter-operator messaging feature for
 * its primary audience.
 *
 * Hoisting both concerns into this small component (mounted from
 * ``App.js`` right below the auth gate) fixes both branches at once.
 */
export default function GlobalUserSideEffects() {
  const { user } = useAuth();

  // Presence heartbeat — 30s cadence, paused while the tab is
  // backgrounded so we don't retain ghost operators after browser
  // idle. Mirrors the previous in-Layout implementation exactly so
  // no server-side changes were needed.
  useEffect(() => {
    if (!user) return undefined;
    let stopped = false;
    const beat = () => {
      if (stopped || document.hidden) return;
      http.post("/users/heartbeat").catch(() => { /* silent */ });
    };
    beat();
    const t = setInterval(beat, 30000);
    const onVis = () => { if (!document.hidden) beat(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stopped = true;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user]);

  if (!user) return null;
  return <MessagesListener />;
}
